import { z } from 'zod';

import { emailSchema, richTextSchema } from './common.js';

export const FEEDBACK_CATEGORIES = ['bug', 'idea', 'copy', 'design', 'praise', 'other'] as const;
export type FeedbackCategory = (typeof FEEDBACK_CATEGORIES)[number];

/**
 * Submission payload for the public feedback launcher. `email` is optional —
 * only collected when the submitter is logged out (an authed submission is
 * attributed via the session). `page_url`/`user_agent` are captured client-side
 * to give triage context.
 */
export const feedbackSubmitSchema = z.object({
  category: z.enum(FEEDBACK_CATEGORIES),
  message: richTextSchema(4000).refine((s) => s.trim().length >= 3, {
    message: 'Tell us a little more.',
  }),
  email: emailSchema.optional(),
  page_url: z.string().max(2048).optional(),
  user_agent: z.string().max(1024).optional(),
});
export type FeedbackSubmitInput = z.infer<typeof feedbackSubmitSchema>;

/** Admin status transition for a feedback row. */
export const feedbackStatusSchema = z.object({
  status: z.enum(['new', 'reviewed', 'archived']),
});
export type FeedbackStatusInput = z.infer<typeof feedbackStatusSchema>;

/**
 * Internal admin comment on a feedback submission. Plain text (rendered as
 * pre-wrap, not markdown); the same schema validates the admin form and the
 * route handler.
 */
export const feedbackCommentSchema = z.object({
  body: richTextSchema(4000).refine((s) => s.trim().length >= 1, {
    message: 'Add a comment before saving.',
  }),
});
export type FeedbackCommentInput = z.infer<typeof feedbackCommentSchema>;
